import { getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { IPaginatedResponse, ISupplierOrder } from 'pages/supplier-intelligence/utils/types';
import type { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/supplier-intelligence/suppliers';

type TData = IPaginatedResponse<ISupplierOrder>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    id: number;
    page: TParam;
    limit: TParam;
    sort: TParam;
    code: TParam;
    restaurantId: TParam;
    subEntityId: TParam;
    subEntityType: TParam;
    status: TParam;
    deliveryDate: TParam;
    createdAt: TParam;
    supplierUserId: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSiSupplierOrdersApi(id: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = 10;
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, code, restaurantId, subEntityId, subEntityType, status, deliveryDate, createdAt, supplierUserId } =
    getPageParams();

  const queryFn = async () => {
    try {
      const params: any = { page, limit };
      if (sort) params.sort = sort;
      if (code) params.code = code;
      if (restaurantId) params.restaurantId = restaurantId;
      if (subEntityId) params.subEntityId = subEntityId;
      if (subEntityType) params.subEntityType = subEntityType;
      if (status) params.status = status;
      if (deliveryDate) params.deliveryDate = deliveryDate;
      if (createdAt) params.createdAt = createdAt;
      if (supplierUserId) params.supplierUserId = supplierUserId;
      const { data } = await http.get(`${endpoint}/${id}/orders`, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper(
    [
      `${endpoint}/${id}/orders`,
      {
        id,
        page,
        limit,
        sort: sort ?? null,
        code: code ?? null,
        restaurantId: restaurantId ?? null,
        subEntityId: subEntityId ?? null,
        subEntityType: subEntityType ?? null,
        status: status ?? null,
        deliveryDate: deliveryDate ?? null,
        createdAt: createdAt ?? null,
        supplierUserId: supplierUserId ?? null,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn && !!id,
      ...options,
    }
  );
}
